import {Color} from '@material-ui/lab/Alert/Alert';
import {
  Table,
  TableWorker,
  Competitor,
  Result,
  Tournament,
  User,
  Standing,
} from './schema-types';

export type CountrySelect = {
  /**
   * Id of the country selected
   */
  id: number;
  /** Iso2 unique code of the country */
  iso2: string;
  /** Name of the country selected */
  name: string;
};

export type Round = {
  /**
   * Index of the round
   */
  index: number;
  /**
   * Array of tables with the players and scores.
   */
  tables: Table[];
};

export type GeneratedRound = {
  /**
   * Index of the round
   */
  index: number;
  /**
   * Array of the generated tables by the worker
   */
  tables: TableWorker[];
};

/*
 ** Forms section
 */
export type IClubForm = {
  /** Name of club */
  name: string;
  /** Country of the club */
  country: string;
  /** State of the club */
  state: string;
  /** City of the club */
  city: string;
};

export type IRegisterForm = {
  /**
   * Fullname of the player
   */
  fullName: string;
  /** Email of the player */
  email: string;
  /** Password of the player */
  password: string;
  /** Gender of the player */
  gender: string;
  /** Phone number of the player */
  phoneNumber: string;
  /** Country of the player */
  country: string;
  /** State of the player */
  state: string;
  /** City of the player */
  city: string;
  /** Document type of the player */
  documentType: string;
  /** Document number of the player */
  document: string;
  /** Clubname of the player if exist */
  clubName: string | undefined;
};

export type ICreateAdminForm = {
  /**
   * Fullname of the admin
   */
  fullName: string;
  /** Email of the admin */
  email: string;
  /**
   * Phone number of the admin
   */
  phoneNumber: string;
  /**
   * Password of the admin
   */
  password: string;
};

export type CreateTournamentForm = {
  /** Nombre del torneo */
  name: string;
  /** Categoría del torneo (Másculino, femenino, mixto) */
  gender: string;
  /** Numero de rondas a las que se jugará el torneo */
  numberOfRounds: number;
  /** Tipo de torneo que se va a jugar */
  tournamentType: string;
  /** Categoria del torneo */
  tournamentCategory: string;
  /** Modalidad del torneo */
  modalityGame: string;
  /** Limite de puntos del torneo */
  pointLimit: number;
  /** Si el torneo tiene final o no */
  hasFinal: boolean;
  /** País donde se va realizar el torneo */
  country: string;
  /** Departamento donde se va realizar el torneo */
  state: string;
  /** Ciudad donde se va realizar el torneo */
  city: string;
  /** Fecha de inicio del torneo */
  startDate: string;
  /** Fecha de fin del torneo */
  finishDate: string;
};

export type FormInputOptions = {
  /**
   * Id of the form
   */
  id: string;
  /**
   * Name of the form
   */
  name: string;
  /**
   * Label to display
   */
  label: string;
  /** Field options */
  fieldOptions: {
    /** Mark if is required property */
    required: boolean;
    /** Message in case that the value is not provided */
    message: string;
  };
  /** Type of the form input */
  type?: string;
  /** Placeholder */
  placeholder?: string;
  /** Options to display */
  options?: SelectOption[];
};

export type SelectOption = {
  /** Valor a mostrar en el selectbox */
  label: string;
  /** Valor a capturar */
  value: any;
};

export type DialogProps = {
  toggle: () => void;
  modalOpen: boolean;
};

export type TournamentConditions = {
  /**
   *  Genero del torneo
   */
  gender: string;
  /**
   * Número de rondas a la que se va a jugar
   */
  numberOfRounds: number;
  /**
   * Tipo de torneo
   */
  tournamentType: string;
  /**
   * Modalidad del torneo
   */
  modalityGame: string;
  /**
   * Número de pintas a la que se va a jugar
   */
  pointLimit: number;
  /**
   * Categoria del torneo: INTERNACIONAL, NACIONAL, DEPARTAMENTAL
   */
  tournamentCategory: string;
};

export type StandingPlayer = Competitor & {
  /**
   * Difference of effectiveness between the closest
   * opponent in the standing
   */
  diffEffect: number;
};

/* App Slice Types */

export type Toast = {
  isOpen: boolean;
  type?: Color;
  message?: string;
};

/* Round Slice Types  */

export type RoundMetaData = {
  /**
   * Id of the tournament of this round belongs to
   */
  idTournament: string;
  /**
   * Current round
   */
  currentRound: number;
  /** 
   Next round to send to the worker 
  */
  nextRound: number;
  /** 
   Indicator is the round is saved 
  */
  saved: boolean;
};

export type Metadata = {
  /**
   * Key of the metadata
   */
  key: string;
  /**
   * Value of the metadata
   */
  value: any;
};

export type OptionValue = {
  /**
   * Label to display in the select
   */
  label: string;
  /**
   * Value to set in the state
   */
  value: any;
};

export type MenuOptions = {
  displayName: string;
  handler: (v: any) => void;
};

/** Enums and Interfaces */

export enum FormNames {
  EMAIL = 'email',
  FULLNAME = 'fullName',
  PASSWORD = 'password',
  GENDER = 'gender',
  PHONE_NUMBER = 'phoneNumber',
  COUNTRY = 'country',
  STATE = 'state',
  CITY = 'city',
  DOCUMENT_TYPE = 'documentType',
  DOCUMENT = 'document',
  CLUBNAME = 'clubName',
}

export interface RoundState {
  /** Round Metadata information */
  metadata: RoundMetaData;
  /** Current generated round payload generated by the worker */
  generatedRound: GeneratedRound | null;
  /** Current loaded round from the server */
  loadedRound: Result[] | null;
  /** Select round index */
  selectedRound: OptionValue;
  /** Standings */
  standings: Standing | null;
}

export interface AppState {
  /** Determines the state of the toast opened */
  toastOpen: Toast | null;
  /** Determines which tournaments are currently playing */
  playingIds: string[];
  /** Selected tournament id */
  selectedTournament: Tournament | null;
  /** Group */
  currentGroup: User[];
  /** Id of the tournament to check the standings */
  selectedStanding: Tournament | null;

  sponsorList: string[];
}
